/* Feral Skin homepage. Small on purpose: reveals, nav state, qty stepper, buy button state. */
(() => {
  const designMode = window.Shopify && window.Shopify.designMode;

  /* ---------- Scroll reveals ---------- */
  let revealObserver;

  function initReveals(scope = document) {
    const els = scope.querySelectorAll('[data-reveal]:not(.is-in)');
    if (designMode || !('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('is-in'));
      return;
    }
    revealObserver =
      revealObserver ||
      new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-in');
            revealObserver.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
      );
    els.forEach((el) => revealObserver.observe(el));
  }

  /* ---------- Nav: border, progress bar, active link ---------- */
  function initNav() {
    const nav = document.querySelector('[data-fs-nav]');
    if (!nav) return;
    const bar = nav.querySelector('.fs-nav__progress');
    let ticking = false;

    const update = () => {
      ticking = false;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      const p = max > 0 ? Math.min(1, window.scrollY / max) : 0;
      if (bar) bar.style.transform = `scaleX(${p})`;
      nav.classList.toggle('is-scrolled', window.scrollY > 8);
    };

    window.addEventListener(
      'scroll',
      () => {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(update);
        }
      },
      { passive: true }
    );
    update();

    const links = [...nav.querySelectorAll('[data-fs-navlink]')];
    if (!links.length || !('IntersectionObserver' in window)) return;
    const byId = new Map(links.map((a) => [a.hash.slice(1), a]));
    const spy = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const link = byId.get(entry.target.id);
          if (!link) return;
          if (entry.isIntersecting) {
            links.forEach((a) => a.removeAttribute('aria-current'));
            link.setAttribute('aria-current', 'true');
          } else if (link.getAttribute('aria-current')) {
            link.removeAttribute('aria-current');
          }
        });
      },
      { rootMargin: '-40% 0px -55% 0px' }
    );
    byId.forEach((_, id) => {
      const target = document.getElementById(id);
      if (target) spy.observe(target);
    });
  }

  /* ---------- Quantity stepper ---------- */
  function initQty(scope = document) {
    scope.querySelectorAll('[data-fs-qty]').forEach((wrap) => {
      const input = wrap.querySelector('input');
      if (!input) return;
      const clamp = (v) => {
        const min = parseInt(input.min, 10) || 1;
        const max = parseInt(input.max, 10) || 99;
        return Math.min(max, Math.max(min, Number.isNaN(v) ? min : v));
      };
      wrap.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-step]');
        if (!btn) return;
        input.value = clamp(parseInt(input.value, 10) + parseInt(btn.dataset.step, 10));
      });
      input.addEventListener('change', () => {
        input.value = clamp(parseInt(input.value, 10));
      });
    });
  }

  /* ---------- Buy button: show it's working ---------- */
  function initBuy(scope = document) {
    scope.querySelectorAll('[data-fs-buy]').forEach((form) => {
      const btn = form.querySelector('.fs-buy__submit');
      if (!btn) return;
      const original = btn.innerHTML;
      form.addEventListener('submit', () => {
        btn.classList.add('is-busy');
        btn.setAttribute('aria-busy', 'true');
        btn.textContent = btn.dataset.busyLabel || original;
      });
      // Coming back with the browser's back button restores the page from cache.
      window.addEventListener('pageshow', (e) => {
        if (!e.persisted) return;
        btn.classList.remove('is-busy');
        btn.removeAttribute('aria-busy');
        btn.innerHTML = original;
      });
    });
  }

  /* ---------- Product photo thumbnails ---------- */
  function initGallery(scope = document) {
    scope.querySelectorAll('[data-fs-gallery]').forEach((gallery) => {
      const img = gallery.querySelector('.fs-media__img');
      if (!img) return;
      gallery.addEventListener('click', (e) => {
        const thumb = e.target.closest('.fs-gallery__thumb');
        if (!thumb) return;
        img.srcset = thumb.dataset.srcset;
        img.src = thumb.dataset.src;
        img.alt = thumb.dataset.alt;
        gallery.querySelectorAll('.fs-gallery__thumb').forEach((t) => {
          const on = t === thumb;
          t.classList.toggle('is-active', on);
          if (on) t.setAttribute('aria-current', 'true');
          else t.removeAttribute('aria-current');
        });
      });
    });
  }

  function init() {
    initReveals();
    initNav();
    initQty();
    initBuy();
    initGallery();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor: re-wire sections as they're edited.
  document.addEventListener('shopify:section:load', (e) => {
    initReveals(e.target);
    initQty(e.target);
    initBuy(e.target);
    initGallery(e.target);
  });
})();
